import { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Divider, Grid, CircularProgress, Alert
} from '@mui/material';
import { Print } from '@mui/icons-material';
import { patientAPI } from '../services/api';
import { useReactToPrint } from 'react-to-print';

function TodaysRecordPrint({ patientId, onClose }) {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const printRef = useRef();

  useEffect(() => {
    loadTodaysRecord();
  }, [patientId]);

  const loadTodaysRecord = async () => {
    try {
      const response = await patientAPI.getTodaysRecord(patientId);
      setRecord(response.data.data);
    } catch (err) {
      setError('Failed to load today\'s record');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Todays_Record_${record?.patient.patient_code}_${new Date().toISOString().split('T')[0]}`
  });

  const calculateAge = (dob) => {
    if (!dob) return 'N/A';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // No need to consolidate medicines anymore as we only have consultation medicines

  if (loading) {
    return (
      <Dialog open maxWidth="md" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (error || !record) {
    return (
      <Dialog open onClose={onClose}>
        <DialogContent>
          <Typography color="error">{error || 'No data available'}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Check if there's any data for today
  if (record.recordType === 'none') {
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Today's Record</DialogTitle>
        <DialogContent>
          <Alert severity="info">
            No consultations or treatment sessions found for today ({new Date(record.date).toLocaleDateString()}).
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Today's Record - {new Date(record.date).toLocaleDateString()}</DialogTitle>
      <DialogContent>
        {/* Printable Content */}
        <Box ref={printRef} sx={{ p: 4, '@media print': { p: 2 } }}>
          {/* Hospital Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" fontWeight="bold">
              Naturopathy Hospital
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              TODAY'S RECORD
            </Typography>
            <Typography variant="caption">
              Date: {new Date(record.date).toLocaleDateString()} | Generated: {new Date().toLocaleString()}
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Patient Information */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="h6" gutterBottom>Patient Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Patient Code</Typography>
                <Typography variant="body2" fontWeight="medium">{record.patient.patient_code}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Name</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {record.patient.first_name} {record.patient.last_name}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Age / Gender</Typography>
                <Typography variant="body2">
                  {calculateAge(record.patient.date_of_birth)} years / {record.patient.gender}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Phone</Typography>
                <Typography variant="body2">{record.patient.phone}</Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Format: Consultation (with or without IPD treatments) */}
          {(record.recordType === 'consultation_only' || record.recordType === 'consultation_with_treatments') && record.consultations?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              {record.consultations.map((consultation, idx) => (
                <Box key={idx} sx={{ mb: 3 }}>
                  <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Doctor</Typography>
                        <Typography variant="body2" fontWeight="medium">{consultation.doctor_name}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Visit Type</Typography>
                        <Typography variant="body2">{consultation.visit_type?.toUpperCase()}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Diagnosis</Typography>
                        <Typography variant="body2">{consultation.diagnosis || 'N/A'}</Typography>
                      </Grid>
                      {consultation.notes && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">Notes</Typography>
                          <Typography variant="body2">{consultation.notes}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>

                  {/* Prescribed Treatments */}
                  {record.prescribedTreatments?.filter(pt => pt.consultation_id === consultation.id).length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Prescribed Treatments
                      </Typography>
                      <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                              <TableCell><strong>Treatment</strong></TableCell>
                              <TableCell><strong>Quantity</strong></TableCell>
                              <TableCell><strong>Notes</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {record.prescribedTreatments
                              .filter(pt => pt.consultation_id === consultation.id)
                              .map((treatment, tIdx) => (
                                <TableRow key={tIdx}>
                                  <TableCell>{treatment.treatment_name}</TableCell>
                                  <TableCell>{treatment.quantity || 1}</TableCell>
                                  <TableCell>{treatment.notes || '-'}</TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {/* Prescribed Packages */}
                  {record.prescribedPackages?.filter(p => p.consultation_id === consultation.id).length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Prescribed Packages
                      </Typography>
                      <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                              <TableCell><strong>Package</strong></TableCell>
                              <TableCell><strong>Notes</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {record.prescribedPackages
                              .filter(p => p.consultation_id === consultation.id)
                              .map((pkg, pIdx) => (
                                <TableRow key={pIdx}>
                                  <TableCell>{pkg.package_name}</TableCell>
                                  <TableCell>{pkg.notes || '-'}</TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {/* Prescribed Medicines */}
                  {record.consultationMedicines?.filter(m => m.consultation_id === consultation.id).length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Prescribed Medicines
                      </Typography>
                      <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                              <TableCell><strong>Medicine</strong></TableCell>
                              <TableCell><strong>Dosage</strong></TableCell>
                              <TableCell><strong>Frequency</strong></TableCell>
                              <TableCell><strong>Duration</strong></TableCell>
                              <TableCell><strong>Route</strong></TableCell>
                              <TableCell><strong>Instructions</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {record.consultationMedicines
                              .filter(m => m.consultation_id === consultation.id)
                              .map((medicine, mIdx) => (
                                <TableRow key={mIdx}>
                                  <TableCell>{medicine.medicine_name}</TableCell>
                                  <TableCell>{medicine.dosage}</TableCell>
                                  <TableCell>{medicine.frequency}</TableCell>
                                  <TableCell>{medicine.duration}</TableCell>
                                  <TableCell>{medicine.route || '-'}</TableCell>
                                  <TableCell>{medicine.instructions || '-'}</TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {/* IPD Treatments Completed Today */}
          {(record.recordType === 'treatments_only' || record.recordType === 'consultation_with_treatments') && record.ipdTreatments?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                IPD Treatments Completed Today
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell><strong>Time Slot</strong></TableCell>
                      <TableCell><strong>Treatment</strong></TableCell>
                      <TableCell><strong>Sequence</strong></TableCell>
                      <TableCell><strong>Completed By</strong></TableCell>
                      <TableCell><strong>Completed At</strong></TableCell>
                      <TableCell><strong>Notes</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {record.ipdTreatments.map((treatment, tIdx) => (
                      <TableRow key={tIdx}>
                        <TableCell>{treatment.time_slot || '-'}</TableCell>
                        <TableCell>{treatment.treatment_name}</TableCell>
                        <TableCell>{treatment.sequence}</TableCell>
                        <TableCell>{treatment.completed_by_name || '-'}</TableCell>
                        <TableCell>
                          {treatment.completed_at
                            ? new Date(treatment.completed_at).toLocaleTimeString()
                            : '-'}
                        </TableCell>
                        <TableCell>{treatment.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Footer */}
          <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              This is a computer-generated document. No signature is required.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          startIcon={<Print />}
          onClick={handlePrint}
        >
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TodaysRecordPrint;
